import type { ActionTree } from 'vuex'

// @ts-ignore
import * as Papa from 'papaparse'
import { ParseError } from 'libphonenumber-js'
import type { ConversionGraphTimeseries, ConversionStats, MessageState } from './types'
import {
  recursivelyPurifyUnlayerDesign,
  getDynamicTagsFromContent,
} from './utils'
import { defaultTimeWindowOptions } from './index'
import type { RootState } from '@/store/modules/types'
import { createTextFile } from '@/utils/html-element/'
import { isValidTzDate } from '@/utils/date/'
import { clone } from '@/utils/helpers'
import { getContext } from '@/utils/general'

let requestCancelToken: any

export const messageActions: ActionTree<MessageState, RootState> = {
  async FETCH_ADDITIONAL_DYNAMIC_TAGS({ rootState, commit }) {
    if (!rootState.authModule.account) { return null }
    const promoterOid = rootState.authModule.account.promoterOid
    const { $api, $arNotification } = getContext()

    try {
      const additionalDynamicTags = await $api.messages.fetchAdditionalDynamicTags(promoterOid)
      commit('SET_ADDITIONAL_DYNAMIC_TAGS', additionalDynamicTags)
    }
    catch (err) {
      console.error(err)
      $arNotification.push({ type: 'error', message: 'Failed to fetch default dynamic tags.' })
      return false
    }
    finally {
      // TODO ?
    }
  },

  async SEND_OUT_MESSAGE({ rootState, state, commit, dispatch }, scratchMessage: ScratchEmailMessage | ScratchSimpleMessage) {
    if (!rootState.authModule.account) { return null }
    const promoterOid = rootState.authModule.account.promoterOid
    const promoterAccountOid = rootState.authModule.account.oid

    const {
      oid,
      customerName,
      meta,
      scheduledAt,
      provider,
    } = scratchMessage

    const body: any = {
      promoterOid,
      promoterAccountOid,
      status: 'in-progress',
      name: 'fan-message',
      customerName,
      provider,
      meta,
    }

    if (scheduledAt) {
      body.status = 'scheduled'
      body.scheduledAt = scheduledAt
    }

    if (body.meta && body.meta.uiStatus) {
      delete body.meta.uiStatus
    }

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_SENDING_OUT_MESSAGE', true)

      // Check if message list exist, if not, stop it
      let hasMessageList = true
      try {
        const res = await $axios.get(`/promoter/${promoterOid}/message-list/${meta.messageListOid}`)
        if (!res.data) {
          hasMessageList = false
        }
      }
      catch (e) {
        hasMessageList = false
      }
      if (!hasMessageList) {
        const campaignOid = meta.initiator?.campaignOid
        dispatch('SHOW_CONFIRM', {
          title: `Could not ${scheduledAt ? 'schedule' : 'send'} message`,
          messageHtml: campaignOid ? 'The selected recipients are unavailable. Please try selecting the recipients again.' : 'Your selected List is not available. Please select another List.',
          hideCancelButton: true,
          confirmButtonText: 'OK',
        }, { root: true })
        return
      }

      if (oid) {
        await $axios.patch(`/promoter/${promoterOid}/task/${oid}`, body)
        dispatch('CONVERSION_SET_ATTRIBUTION_PARAMS', oid)
      }
      else {
        const { data } = await $axios.post(`/promoter/${promoterOid}/task`, body)
        // SMS can be sent in one action and it can not trigger in patch CONVERSION_SET_ATTRIBUTION_PARAMS upper
        if (data.oid && data.provider === 'sms') {
          dispatch('CONVERSION_SET_ATTRIBUTION_PARAMS', data.oid)
        }
      }
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to send message' })
      return false
    }
    finally {
      commit('SET_IS_SENDING_OUT_MESSAGE', false)
    }
  },

  async SAVE_MESSAGE_DRAFT({ rootState, state, commit, rootGetters }, {
    provider,
    oid,
    customerName,
    meta,
    scheduledAt,
    status,
    isScheduled,
  }) {
    if (!rootState.authModule.account) { return null }
    const promoterOid = rootState.authModule.account.promoterOid
    const promoterAccountOid = rootState.authModule.account.oid

    const jsonBody: any = {
      promoterOid,
      promoterAccountOid,
      provider,
      meta,
      customerName,
      name: 'fan-message',
      status: 'draft',
      isScheduled,
    }

    console.log(jsonBody)

    // Handle scheduling information:
    // If both scheduledAt and timeZone exist, keep the scheduledAt
    // If only one exists, make sure we save both or neither
    if (scheduledAt && meta.presentation.timeZone) {
      jsonBody.scheduledAt = scheduledAt
      // Ensure timeZone is included in the JSON body
      jsonBody.meta.presentation.timeZone = meta.presentation.timeZone
    }
    else if (scheduledAt && !meta.presentation.timeZone && isScheduled) {
      // If scheduled flag was set but timezone is missing, use default timezone
      const defaultTimezone = rootState.authModule.regionSettings?.additionalInfo?.defaultTimezone
      if (defaultTimezone) {
        jsonBody.scheduledAt = scheduledAt
        jsonBody.meta.presentation.timeZone = defaultTimezone
      }
      else {
        jsonBody.scheduledAt = null
      }
    }
    else {
      jsonBody.scheduledAt = null
    }

    const uri = `/promoter/${promoterOid}/task`
    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_SAVING_MESSAGE_AS_DRAFT', true)

      // save promoter task first
      const response = oid
        ? await $axios.patch(`${uri}/${oid}`, jsonBody)
        : await $axios.post(uri, jsonBody)
      const created = response.status === 201

      // save linked promoter task messages
      if (rootGetters['authModule/isFeatureEnabled'](['messages', 'ab-emails'])) {
        const promoterMessages = []

        if (state.scratchEmailMessage.abEmail) {
          for (const key in state.scratchEmailMessageVariants) {
            const scratchMessage = state.scratchEmailMessageVariants[key]
            const promoterMessage: any = {
              variant: key,
              meta: {
                messageBody: clone(scratchMessage.meta.messageBody),
                templateResourceOid: scratchMessage.meta.templateResourceOid,
              },
            }

            if (state.scratchEmailMessage.abEmailType === 'subject') {
              promoterMessage.meta.presentation = clone(state.scratchEmailMessage.meta.presentation)
              promoterMessage.treatment = 'email-subject'
            }
            else {
              promoterMessage.meta.presentation = clone(scratchMessage.meta.presentation)
              promoterMessage.meta.messageBody = clone(state.scratchEmailMessage.meta.messageBody)
              promoterMessage.treatment = 'email-design'
            }

            promoterMessages.push(promoterMessage)
          }
        }
        else {
          promoterMessages.push({
            meta: {
              presentation: clone(state.scratchEmailMessage.meta.presentation),
              messageBody: clone(state.scratchEmailMessage.meta.messageBody),
              templateResourceOid: state.scratchEmailMessage.meta.templateResourceOid,
            },
          })
        }

        const taskOid = oid ? oid : response.data.oid
        await $axios.post(`/promoter/${promoterOid}/task/${taskOid}/messages`, { messages: promoterMessages })
      }

      if (created) {
        $arNotification.push({ type: 'success', message: 'Message saved as draft' })
      }

      return response
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to save message as draft' })
      return false
    }
    finally {
      commit('SET_IS_SAVING_MESSAGE_AS_DRAFT', false)
    }
  },

  async SEND_TEST_EMAIL({ rootState, commit }, {
    recipients,
    type,
    taskOid,
    automationBlockOid,
    meta,
    html,
  }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()
    try {
      commit('SET_IS_SENDING_TEST_EMAIL', true)

      const jsonBody = {
        taskOid: taskOid,
        automationBlockOid: automationBlockOid,
        type,
        recipients,
      }

      // @ts-ignore
      await $axios.sg.post(`/promoter/${promoterOid}/test-message`, jsonBody)
      return true
    }
    catch (error) {
      $arNotification.push({
        type: 'error',
        message: 'Failed to send test message',
      })
      return false
    }
    finally {
      commit('SET_IS_SENDING_TEST_EMAIL', false)
    }
  },

  async FETCH_SMS_MESSAGE_PREVIEW(
    { rootState, commit, getters, state },
    { editSmsMessage, cost = false }: { editSmsMessage: ScratchSimpleMessage, cost: boolean },
  ) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()
    try {
      const advancedTargetingFilter = getters.getCurrentFilterExpression || null

      if (cost) {
        commit('SET_SMS_COST_FAILED_TO_FETCH', false)
        commit('SET_IS_FETCHING_SMS_COST', true)
      }

      const params: any = {
        $provider: 'sms',
        $body: editSmsMessage.meta.messageBody || '',
        $messageListOid: null,
        $cost: cost || null,
        $bucketOid: editSmsMessage.meta?.bucketOid,
        $audienceFilter: cost ? advancedTargetingFilter : null,
        $senderOid: editSmsMessage.meta?.promoterSmsSenderOid || null,
      }
      if (editSmsMessage.meta.messageListOid) {
        params.$messageListOid = editSmsMessage.meta.messageListOid
      }
      if (editSmsMessage.meta.tagMaxWidthMap && Object.keys(editSmsMessage.meta.tagMaxWidthMap).length > 1) {
        try {
          const encodedString = JSON.stringify(editSmsMessage.meta.tagMaxWidthMap)
          params.$tagMaxWidthMap = encodedString
        }
        catch (e) {
          console.log(e)
        }
      }

      // @ts-ignore
      const { data } = await $axios.cn.get(`/promoter/${promoterOid}/message-preview/`, {
        params,
      })

      if (!editSmsMessage.meta?.bucketOid) {
        data.image = null
      }

      commit('PUT_SMS_MESSAGE_PREVIEW', data)
    }
    catch (error) {
      console.error(error)

      // @ts-ignore
      if (error?.response?.data && error.response.data['internal-code'] === 'unprocessable-resource-field' && error.response.data?.message?.details) {
        // @ts-ignore
        this.$arNotification.push({ type: 'warning', message: error.response.data?.message?.details })
      }
      else {
        this.$arNotification.push({ type: 'error', message: 'Failed to fetch SMS message preview' })
      }

      if (cost) {
        commit('SET_SMS_COST_FAILED_TO_FETCH', true)
      }
    }
    finally {
      if (cost) {
        commit('SET_IS_FETCHING_SMS_COST', false)
      }
    }
  },

  async FETCH_MESSAGE_PREVIEW_BY_BUCKET({ rootState, commit }, { bucketOid, provider }) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.auth.account
    const { $axios, $arNotification } = getContext()

    try {
      const { data } = await $axios.get(`/promoter/${promoterOid}/message-preview/`, {
        params: {
          $provider: provider,
          $bucketOid: bucketOid,
          $body: '',
        },
      })
      commit('PUT_SMS_MESSAGE_PREVIEW', data)
    }
    catch (error) {
      console.error(error)
      this.$arNotification.push({ type: 'error', message: 'Failed to fetch message preview by bucket' })
    }
  },

  async FETCH_MORE_MESSAGES(
    { state, rootState, commit },
    {
      status = null,
      search = null,
      top = 20,
      select = null,
      provider = null,
      reload = false,
      more = false,
      linkedEvent = null,
      orderBy = null,
      skip = undefined,
    },
  ) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    let order = orderBy

    // If no orderBy provided, use status-based default sorting
    if (!order) {
      if (status === 'draft')
        order = 'sys_mtime DESC'
      else if (status === 'completed')
        order = 'started DESC NULLS LAST'
      else if (status === 'scheduled')
        order = 'scheduled_at DESC'
      else if (status === 'in-progress')
        order = 'sys_ctime DESC'
      else if (status === 'failed') {
        order = 'sys_mtime DESC'
        status = 'failed,cancelled'
      }
    }

    if (state.isFetchingMessages) {
      console.error('FETCH_MORE_MESSAGES cancelled due to existing pending request')
      return
    }

    // If it's not load more, pls reset messages
    if (reload) {
      commit('RESET_MESSAGES')
    }

    if (state.hasFetchMessagesFailed) {
      console.error(`FETCH_MORE_MESSAGES cancelled due to previous failed request.`)
      return
    }

    const { $axios, $arNotification } = getContext()

    const controller = new AbortController()
    requestCancelToken = controller

    try {
      commit('SET_IS_FETCHING_MESSAGES', true)
      const uri = `/promoter/${promoterOid}/fan-message-search`
      const params = {
        $top: top,
        $skip: typeof skip === 'number' ? skip : (more ? state.messages.length : 0),
        $count: true,
        $select: select || null,
        $search: search || null,
        $status: status || null,
        $provider: provider || null,
        $orderby: order || 'sys_mtime DESC',
        $linkedEvent: linkedEvent || state.overviewLinkedEvent || null,
      }

      const { data } = await $axios.get(uri, {
        params,
        signal: controller.signal,
        // cancelToken: new $axios.CancelToken(c => {
        //   requestCancelToken = c;
        // }),
      })

      // Extract rows and count from the response if it has the { count, rows } structure
      const messages = data.rows || data
      const totalCount = data.count !== undefined ? data.count : messages.length

      commit('CONCAT_MESSAGES', messages)
      commit('SET_TOTAL_MESSAGES_COUNT', totalCount)

      if (messages.length === 0) {
        commit('SET_IS_NO_MORE_MESSAGES', true)
      }
    }
    catch (error: any) {
      if (error) {
        if (error.name === 'AbortError') {
          console.error('Fetch message aborted')
        }
        else {
          console.error(error)
          $arNotification.push({ type: 'error', message: 'Failed to fetch messages' })
          commit('SET_HAS_FETCH_MESSAGES_FAILED', true)
        }
        return
      }
      // if (!$axios.isCancel(error)) {
      // console.error(error);
      // $arNotification.push({ type: 'error', message: 'Failed to fetch messages' });
      // commit('SET_HAS_FETCH_MESSAGES_FAILED', true);
      // }
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGES', false)
    }
  },

  async CANCEL_MESSAGES_FETCHING() {
    // console.log("CANCEL_MESSAGES_FETCHING | controller", controller)
    // if (!controller.signal.aborted) {
    requestCancelToken.abort()
    // }
  },

  async FETCH_GROUP_MESSAGES(
    { state, rootState, commit },
    {
      groupOid = null,
      eventOids = [],
      status = null,
      search = null,
      top = 50,
      skip = 0,
      provider = null,
      reload = false,
      more = false,
      sortBy = 'status ASC',
    },
  ) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    if (!groupOid) {
      console.error('FETCH_GROUP_MESSAGES: groupOid is required')
      return
    }

    if (state.isFetchingMessages) {
      console.error('FETCH_GROUP_MESSAGES cancelled due to existing pending request')
      return
    }

    // If it's reload, reset messages
    if (reload) {
      commit('RESET_MESSAGES')
    }

    if (state.hasFetchMessagesFailed) {
      console.error('FETCH_GROUP_MESSAGES cancelled due to previous failed request.')
      return
    }

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_MESSAGES', true)

      sortBy = sortBy.replace('createdDate', 'sysCtime')

      const payload: Record<string, any> = {
        $top: top,
        $skip: skip,
        $orderby: sortBy,
        $count: true,
        $select: 'oid,customerName,provider,status,sysCtime,sysMtime,scheduledAt,statusDetails,meta',
      }

      // Always include event-oids when provided (including empty array)
      // - undefined/not provided = select all events (backend default)
      // - [] = select no events (explicit empty selection)
      // - [1,2,3] = select specific events
      if (eventOids !== undefined) {
        payload['event-oids'] = eventOids
      }

      // Add optional filters
      if (search) {
        payload['$search'] = search
      }

      if (status) {
        payload['$status'] = status
      }

      if (provider) {
        payload['message-provider'] = provider
      }

      const { data } = await $axios.post(
        `/promoter/${promoterOid}/event-group/${groupOid}/message`,
        payload,
      )

      // Backend now returns standard format {rows, count}
      const messages = data?.rows || []
      const totalCount = data?.count || 0

      if (reload) {
        // Reset and set new messages
        commit('RESET_MESSAGES')
        commit('CONCAT_MESSAGES', messages)
        commit('SET_TOTAL_MESSAGES_COUNT', totalCount)
      }
      else {
        // Append to existing messages
        commit('CONCAT_MESSAGES', messages)
      }

      if (messages.length === 0) {
        commit('SET_IS_NO_MORE_MESSAGES', true)
      }

      return data
    }
    catch (error: any) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch group messages' })
      commit('SET_HAS_FETCH_MESSAGES_FAILED', true)
      return null
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGES', false)
    }
  },

  async FETCH_MESSAGES_OVERVIEW(
    { state, rootState, commit },
    {
      search = null,
      provider = null,
      limit = { 'draft': 3, 'completed': 5, 'in-progress': 'all', 'scheduled': 3 },
    },
  ) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    if (state.isFetchingOverviewMessages) {
      console.error('FETCH_MESSAGES_OVERVIEW cancelled due to existing pending request')
      return
    }

    if (state.hasFetchOverviewMessagesFailed) {
      console.error(`FETCH_MESSAGES_OVERVIEW cancelled due to previous failed request.`)
      return
    }
    const { $axios, $arNotification } = getContext()
    const controller = new AbortController()
    requestCancelToken = controller

    try {
      commit('SET_IS_FETCHING_OVERVIEW_MESSAGES', true)

      const uri = `/promoter/${promoterOid}/fan-message-search`
      const messages = {}

      const requests = Object.entries(limit).map(([statusKey, top]) => {
        const status = String(statusKey)

        let order
        if (status === 'draft')
          order = 'sys_mtime DESC'
        else if (status === 'completed')
          order = 'started DESC NULLS LAST'
        else if (status === 'scheduled')
          order = 'scheduled_at ASC'
        else if (status === 'in-progress')
          order = 'sys_mtime DESC'

        const params = {
          $top: top,
          $skip: 0,
          $count: true,
          $search: search || null,
          $status: status || null,
          $provider: provider || null,
          $orderby: order || 'sys_ctime DESC',
          $linkedEvent: state.overviewLinkedEvent || null,
        }

        // Return the promise without awaiting it
        return $axios.get(uri, {
          params,
          signal: controller.signal,
          // cancelToken: new $axios.CancelToken(c => {
          //   requestCancelToken = c;
          // }),
        }).then(({ data }) => {
          // Ugly but...
          (messages as any)[statusKey] = data
        })
      })

      // Wait for all requests to finish
      await Promise.all(requests)

      commit('SET_OVERVIEW_MESSAGES', messages)
    }
    catch (error: any) {
      if (error) {
        if (error.name === 'AbortError') {
          console.error('Fetch message aborted')
        }
        else {
          console.error(error)
          $arNotification.push({ type: 'error', message: 'Failed to fetch messages' })
          commit('SET_HAS_FETCH_OVERVIEW_MESSAGES_FAILED', true)
        }
        return
      }
      // if (!$axios.isCancel(error)) {
      // console.error(error);
      // $arNotification.push({ type: 'error', message: 'Failed to fetch messages' });
      // commit('SET_HAS_FETCH_OVERVIEW_MESSAGES_FAILED', true);
      // }
    }
    finally {
      commit('SET_IS_FETCHING_OVERVIEW_MESSAGES', false)
    }
  },

  async FETCH_MESSAGE({ rootState, commit, rootGetters }, oid) {
    if (!rootState.authModule.account) {
      return
    }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()
    if (isNaN(oid)) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch message' })
      return null
    }

    const uri = `/promoter/${promoterOid}/task/${oid}`

    commit('SET_IS_FETCHING_MESSAGE', true)

    try {
      // @ts-ignore
      const { data } = await $axios.get(uri)

      if (data?.meta?.presentation?.templateType === 'unlayer') {
        recursivelyPurifyUnlayerDesign(null, data.meta.presentation.template)
      }

      if (rootGetters['authModule/isFeatureEnabled'](['messages', 'ab-emails'])) {
        const pmResponse = await $axios.get(`/promoter/${promoterOid}/task/${oid}/messages`)
        const promoterMessages = pmResponse.data

        if (promoterMessages.length > 1) {
          data.abEmail = true
          data.abEmailType = promoterMessages[0].treatment === 'email-subject' ? 'subject' : 'design'

          await commit('CREATE_AB_EMAIL_MESSAGE', { message: data, promoterMessages: promoterMessages })
        }
      }

      await commit('SET_CURRENT_SELECTED_MESSAGE', data)
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch message' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGE', false)
    }
  },

  async CLONE_MESSAGE({ rootState, commit }, taskOid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const uri = `/promoter/${promoterOid}/task/${taskOid}/clone`
    const { $axios, $arNotification } = getContext()

    try {
      await $axios.post(uri)

      $arNotification.push({
        type: 'success',
        message: 'Message successfully duplicated',
      })
    }
    catch (error: any) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: `Failed to duplicate message: ${error.message}`,
      })
    }
  },
  async ARCHIVE_MESSAGE({ rootState, commit }, taskOid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const uri = `/promoter/${promoterOid}/task/${taskOid}`
    const { $axios, $arNotification } = getContext()

    try {
      await $axios.patch(uri, {
        status: 'archived',
      })

      $arNotification.push({
        type: 'success',
        message: `Successfully archived message`,
      })
      commit('REMOVE_MESSAGE_FROM_MESSAGES', taskOid)
    }
    catch (error: any) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: `Failed to archive message: ${error.message}`,
      })
    }
  },
  async CANCEL_MESSAGE({ rootState }, { taskOid, isResetScheduledAt = true }) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const uri = `/promoter/${promoterOid}/task/${taskOid}`

    const data: { status: string, scheduledAt?: string } = {
      status: 'draft',
    }

    if (isResetScheduledAt) {
      data.scheduledAt = null
    }

    const { $axios, $arNotification } = getContext()

    try {
      await $axios.patch(uri, data)
      $arNotification.push({
        type: 'success',
        message: `Successfully cancelled message`,
      })
    }
    catch (error: any) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: `Failed to cancel message: ${error.message}`,
      })
    }
  },
  // Email Html
  async GENERATE_SCRATCH_EMAIL_TEMPLATE({ rootState, commit }, unlayerDesign: any) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()
    try {
      const unlayerExportHtml = await $api.unlayer.exportHtml(unlayerDesign)
      commit('SET_SCRATCH_EMAIL_TEMPLATE', unlayerExportHtml?.html)
      return true
    }
    catch (error) {
      $arNotification.push({
        type: 'error',
        message: 'Failed to generate scratch email template',
      })
      return false
    }
  },
  async FETCH_SAVED_EMAIL_TEMPLATE({ dispatch, commit }, oid) {
    const { $arNotification } = getContext()
    try {
      commit('SET_IS_FETCHING_SAVED_EMAIL', true)
      commit('SET_SAVED_EMAIL', null)
      const html = await dispatch('GET_ASSET', { oid }, { root: true })
      commit('SET_SAVED_EMAIL', html)
    }
    catch (error) {
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch email template',
      })
    }
    finally {
      commit('SET_IS_FETCHING_SAVED_EMAIL', false)
    }
  },

  async FETCH_BEEFREE_EMAIL_TEMPLATE_FROM_URL({ rootState, dispatch, commit }, oid) {
    const { $arNotification } = getContext()
    try {
      commit('SET_IS_FETCHING_SAVED_EMAIL', true)
      commit('SET_SAVED_EMAIL', null)
      const { promoterOid } = rootState.authModule.account
      const bucketEmailTemplateUrl = await dispatch('GET_BUCKET_EMAIL_TEMPLATE_ASSET', { promoterOid, oid }, { root: true })
      let html = null
      if (bucketEmailTemplateUrl) {
        let xmlHttp
        xmlHttp = new XMLHttpRequest()
        xmlHttp.open('GET', bucketEmailTemplateUrl, false)
        xmlHttp.send()
        html = xmlHttp.responseText
      }
      commit('SET_SAVED_EMAIL', html)
    }
    catch (error) {
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch email template',
      })
    }
    finally {
      commit('SET_IS_FETCHING_SAVED_EMAIL', false)
    }
  },

  async FETCH_SAVED_BEEFREE_EMAIL_TEMPLATE({ dispatch, commit }, templateHtml) {
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_SAVED_EMAIL', true)
      const html = await $api.beefree.exportHtml(templateHtml)
      commit('SET_SAVED_EMAIL', html)
    }
    catch (error) {
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch email template',
      })
    }
    finally {
      commit('SET_IS_FETCHING_SAVED_EMAIL', false)
    }
  },
  async SWITCH_AB_EMAIL({ dispatch, commit, state, rootState }, data) {
    try {
      commit('SWITCH_AB_EMAIL_STATE', data)
    }
    catch (error) {
      this.$arNotification.push({
        type: 'error',
        message: 'Failed to switch templates',
      })
    }
  },
  async UPDATE_EMAIL_TEMPLATE_IN_SCRATCH_EMAIL_MESSAGE({ dispatch, commit, state, rootState }, { emailHtml, previewHtml, footerHtml, version = 1 }) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    // If you have a saved template, we have to delete it, since we will no longer need it
    // if (state.scratchEmailMessage.meta.templateResourceOid) {
    // 20210303: DO NOT DO THIS AT THE MOMENT! this would probably delete asset from other bucket.
    // dispatch('DELETE_ASSET', state.scratchEmailMessage.meta.templateResourceOid, { root: true });
    // }
    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_UPDATING_EMAIL', true)
      let resource = null

      if (state.scratchEmailMessage.meta?.presentation?.templateType === 'beefree') {
        // Beefree pushes the template to the /bucket-email-template
        resource = await $api.beefree.addNewTemplate(
          promoterOid,
          {
            meta: {
              name: state.scratchEmailMessage.name,
              template: state.scratchEmailMessage.meta.presentation.template,
              version: version,
              previewHtml,
              footerHtml,
            },
            taskOid: state.scratchEmailMessage.oid ? state.scratchEmailMessage.oid : null,
          },
          version,
        )
      }
      else {
        if (!emailHtml || emailHtml === 'undefined') {
          throw 'template emailHtml undefined'
        }

        // we need to create a new HTML asset at every save/send
        // because of how our S3 buckets work - we can only
        // PUT to the bucket resource for a limited period.
        const file = createTextFile({
          text: emailHtml,
          type: 'text/html',
          name: 'email_html', // Name doesn't matter here
        })

        if (!file) {
          throw 'template blob file undefined'
        }

        resource = await dispatch('UPLOAD_ASSET', {
          assetType: 'email-template',
          contentType: 'text/html',
          file,
        }, { root: true })
      }

      if (resource) {
        commit('PATCH_SCRATCH_EMAIL_MESSAGE', {
          meta: {
            templateResourceOid: resource.oid,
          },
        })
      }
      return resource
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to update email template',
      })
      return false
    }
    finally {
      commit('SET_IS_UPDATING_EMAIL', false)
    }
  },
  async FETCH_CSV_PREVIEW_CONTACTS({ commit, rootState }, dynamicTagsCsvOid: number) {
    if (!rootState.authModule.account) { return null }
    const promoterOid = rootState.authModule.account.promoterOid
    const { $api, $arNotification } = getContext()
    try {
      const preSignedUrl = await $api.buckets.generatePreSignedUrl(promoterOid, dynamicTagsCsvOid, 'get')

      const csvString = await $api.csv.stream(preSignedUrl, 25)
      const csvData = Papa.parse<string[]>(csvString, { header: false }).data

      const headers: string[] = csvData[0]
      const rows: string[][] = csvData.splice(1).filter((row: any[]) => {
        if (row.length === 0) {
          return false
        }
        return true
      })

      commit('SET_CSV_PREVIEW_CONTACTS', { headers, rows })
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch dynamic tags csv',
      })
    }
  },
  async RENEW_AVAILABLE_FALLBACK_DYNAMIC_TAGS_FOR_EMAIL_MESSAGE({ commit, state }, message: string) {
    let tagsFromBody: string[] = []
    let tagsFromSubject: string[] = []

    if (state.scratchEmailMessage.abEmail && state.scratchEmailMessage.abEmailType === 'design') {
      const jsonTemplate = (template: object | string) => {
        return typeof template === 'string'
          ? template
          : JSON.stringify(template)
      }

      const designA = getDynamicTagsFromContent(jsonTemplate(state.scratchEmailMessageVariants['A'].meta?.presentation?.template ?? ''))
      const designB = getDynamicTagsFromContent(jsonTemplate(state.scratchEmailMessageVariants['B'].meta?.presentation?.template ?? ''))
      tagsFromBody = Array.from(new Set([...designA, ...designB]))
    }
    else {
      tagsFromBody = getDynamicTagsFromContent(state.scratchEmail)
    }

    if (state.scratchEmailMessage.abEmail && state.scratchEmailMessage.abEmailType === 'subject') {
      const subjectsA = getDynamicTagsFromContent(state.scratchEmailMessageVariants['A'].meta?.messageBody?.subject ?? '')
      const subjectsB = getDynamicTagsFromContent(state.scratchEmailMessageVariants['B'].meta?.messageBody?.subject ?? '')
      tagsFromSubject = Array.from(new Set([...subjectsA, ...subjectsB]))
    }
    else {
      tagsFromSubject = getDynamicTagsFromContent(state.scratchEmailMessage.meta?.messageBody?.subject ?? '')
    }

    const dynamicTags: any[] = Array.from(new Set([...tagsFromBody, ...tagsFromSubject]))
    const dynamicTagsWithoutEmailAddress = dynamicTags.filter(tag => tag !== 'email_address')
    commit('SET_FALLBACK_DYNAMIC_TAGS_FOR_SCRATCH_EMAIL_MESSAGE', dynamicTagsWithoutEmailAddress)
    commit('TIDY_UP_FALLBACK_DYNAMIC_TAGS_IN_EMAIL_MESSAGE', dynamicTagsWithoutEmailAddress)
  },
  async RENEW_AVAILABLE_FALLBACK_DYNAMIC_TAGS_FOR_SIMPLE_MESSAGE({ commit }, message: string) {
    const dynamicTags = getDynamicTagsFromContent(message)
    const dynamicTagsWithoutMobileNumber = dynamicTags.filter(tag => tag !== 'mobile_number')
    commit('SET_FALLBACK_DYNAMIC_TAGS_FOR_SCRATCH_SIMPLE_MESSAGE', dynamicTagsWithoutMobileNumber)
    commit('TIDY_UP_FALLBACK_DYNAMIC_TAGS_IN_SIMPLE_MESSAGE', dynamicTagsWithoutMobileNumber)
  },
  async FETCH_MESSAGE_ACTION_STATS_TIMESERIES({ commit, rootState }, oid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    if (isNaN(oid)) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch message action stats timeseries' })
      return null
    }

    commit('SET_IS_FETCHING_MESSAGE_ACTION_STATS_TIMESERIES', true)
    try {
      const uri = `/promoter/${promoterOid}/fan-message-action-stats-time-series`
      // @ts-ignore
      const { data } = await $axios.get(uri, {
        params: {
          $filter: `task-oid=${oid}`,
        },
      })

      await commit('SET_MESSAGE_ACTION_STATS_TIMESERIES', data[0])
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch message action stats timeseries' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGE_ACTION_STATS_TIMESERIES', false)
    }
  },
  async FETCH_MESSAGE_DELIVERY_STATS({ commit, rootState }, oid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    if (isNaN(oid)) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch message delivery stats' })
      return null
    }

    const uri = `/promoter/${promoterOid}/message-delivery-stats`

    commit('SET_IS_FETCHING_MESSAGE_DELIVERY_STATS', true)
    try {
      const params = {
        $filter: `promoterOid = ${promoterOid} AND taskOid = ${oid}`,
      }

      // @ts-ignore
      const { data } = await $axios.get(uri, { params })

      await commit('SET_MESSAGE_DELIVERY_STATS', data[0])
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch message delivery stats' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGE_DELIVERY_STATS', false)
    }
  },

  async CONVERSION_SET_ATTRIBUTION_PARAMS({ commit, rootState, state }, messageOid: string) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    const eventsData = state.conversionSelectedEvents.map(oid => ({ targetType: 'event-purchase', targetOid: Number(oid) }))

    const linksData = state.conversionSelectedLinks.map((oid) => {
      const index = state.messageLinks.findIndex(data => data.oid === oid)
      return state.messageLinks[index].targetUrl
    })

    const timeWindow = state.conversionTimeWindow

    const uri = `/promoter/${promoterOid}/attribution`
    commit('SET_IS_FETCHING_CONVERSIONS_STATS', true)
    const { $axios, $arNotification } = getContext()

    try {
      const params = {
        sourceType: 'fan-message',
        sourceOid: Number(messageOid),
        targets: eventsData,
        links: linksData,
      }

      await $axios.post(uri, params)

      // Set linked-events in meta of message
      await $axios.post(`/task/${messageOid}/update-message-linked-events`, { eventsIds: state.conversionSelectedEvents })

      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to set conversion events' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CONVERSIONS_STATS', false)
    }
  },

  async FETCH_CONVERSION_STATS_TIMESERIES({ commit, rootState }, messageOid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    if (isNaN(messageOid)) {
      this.$arNotification.push({ type: 'error', message: 'Failed to fetch conversion graph' })
      return null
    }
    const uri = `/promoter/${promoterOid}/attribution-stats`
    commit('SET_IS_FETCHING_CONVERSIONS_GRAPH', true)
    const { $axios, $arNotification } = getContext()

    try {
      const params = {
        sourceType: 'fan-message',
        sourceOid: Number(messageOid),
      }

      const { data } = await $axios.post(uri, params)

      let sortedData = null
      if (data) {
        sortedData = data.reduce((acc: any, record: any) => {
          const timeWindow = record.timeWindow
          if (!acc[timeWindow]) {
            acc[timeWindow] = []
          }
          acc[timeWindow].push(record)
          return acc
        }, {})

        commit('SET_CONVERSION_GRAPH_TIMESERIES_BY_TIME_WINDOWS', sortedData)
      }

      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch conversion graph' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CONVERSIONS_GRAPH', false)
    }
  },

  // Fetch data for conversion table
  async FETCH_CONVERSION_RECIPIENTS({ commit, state, rootState }, messageOid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    if (isNaN(messageOid)) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch conversion table' })
      return null
    }
    const uri = `/promoter/${promoterOid}/attribution-data?$search=${state.conversionTableSearch}&$count=true`
    commit('SET_IS_FETCHING_CONVERSIONS_STATS', true)
    try {
      const params = {
        sourceType: 'fan-message',
        sourceOid: Number(messageOid),
      }

      const { data } = await $axios.post(uri, params)
      commit('SET_CONVERSIONS_TABLE_DATA', data.data)

      commit('SET_CONVERSION_TABLE_COUNT', data.count ? data.count : 0)

      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch conversion table' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CONVERSIONS_STATS', false)
    }
  },

  async FETCH_CONVERSION_STATISTICS({ commit, state, rootState, dispatch }, { messageOid, isSetFromSaved = false }) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    if (isNaN(messageOid)) {
      this.$arNotification.push({ type: 'error', message: 'Failed to fetch conversion statistics, message id is incorrect' })
      return null
    }

    const uri = `/promoter/${promoterOid}/attribution`
    commit('SET_IS_FETCHING_CONVERSIONS_STATS', true)

    try {
      const params = {
        sourceType: 'fan-message',
        sourceOid: Number(messageOid),
      }

      const { data } = await $axios.get(uri, {
        params,
      })

      if (data?.nextUpdateStartDate) {
        commit('SET_CONVERSIONS_NEXT_UPDATE_START_DATE', data.nextUpdateStartDate)
      }

      let sortedData = null
      if (data?.astp) {
        sortedData = data.astp.reduce((acc: any, record: any) => {
          const timeWindow = record.timeWindow
          if (!acc[timeWindow]) {
            acc[timeWindow] = []
          }
          acc[timeWindow].push(record)
          return acc
        }, {})

        commit('SET_CONVERSIONS_STATS_BY_TIME_WINDOWS', sortedData)
      }

      // Update store with selected events
      const selectedEvents = !data?.astp
        ? []
        : Array.from(
            new Set(
              data.astp.map((event: any) => event.targetOid),
            ),
          )
      if (isSetFromSaved && selectedEvents.length) {
        commit('SET_CONVERSIONS_SELECTED_EVENTS', selectedEvents)
      }

      if (data?.status === 'in-progress') {
        commit('SET_IS_CALCULATIONS_FINISHED', false)
      }
      else {
        commit('SET_IS_CALCULATIONS_FINISHED', true)
      }

      return data
    }
    catch (error) {
      commit('SET_IS_CALCULATIONS_FINISHED', true)
      // disabled this error popup, we'll show a nicer message in the UI via a <no-content-section>.
      // this.$arNotification.push({ type: 'error', message: 'Failed to fetch conversion statistic' });
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CONVERSIONS_STATS', false)
    }
  },

  async FETCH_AB_STATS({ commit, state, rootState }, messageOid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    if (isNaN(messageOid)) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch ab statistics' })
      return null
    }
    const uri = `/promoter/${promoterOid}/promoter-message-stats-realtime/${messageOid}`
    commit('SET_IS_FETCHING_AB_MESSAGE', true)
    try {
      const params = {}

      const { data } = await $axios.get(uri, params)
      commit('SET_STATS_AB', data)

      return true
    }
    catch (error) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch AB statistics' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_AB_MESSAGE', false)
    }
  },

  async UPLOAD_HTML_EMAIL(
    { commit, rootState },
    { messageOid, file },
  ) {
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()
    if (isNaN(messageOid)) {
      $arNotification.push({ type: 'error', message: 'Failed to upload html email' })
      return null
    }

    const uri = `/promoter/${promoterOid}/task/${messageOid}/upload-email-html`
    commit('SET_HTML_EMAIL_UPLOAD_FAILED', false)
    commit('SET_IS_UPLOADING_HTML_EMAIL', true)

    try {
      const config = {
        headers: {
          'Content-Type': file.type,
        },
      }

      const { data } = await $axios.post(uri, file, config)
      commit('SET_HTML_EMAIL', data)

      return true
    }
    catch (error) {
      $arNotification.push({ type: 'error', message: 'Failed to upload html email' })
      commit('SET_HTML_EMAIL_UPLOAD_FAILED', true)
      return false
    }
    finally {
      commit('SET_IS_UPLOADING_HTML_EMAIL', false)
    }
  },

  async CHECK_EMAIL_DOMAIN_RESTRICTED(
    { commit, rootState },
    emailAddress,
  ) {
    const { promoterOid } = rootState.authModule.account
    if (!promoterOid) {
      return
    }

    if (!emailAddress) {
      console.error(`CHECK_EMAIL_DOMAIN_RESTRICTED promoter ${promoterOid} - EMAIL CANNOT BE NULL`)
      commit('SET_EMAIL_DOMAIN_RESTRICTED', false)
      return
    }
    const { $axios } = getContext()

    try {
      const { data } = await $axios.get(`/promoter/${promoterOid}/email-domain-restricted`, {
        params: { emailAddress },
      })
      const restricted = data?.restricted || data?.unauthenticated
      const reason = data?.reason
      if (reason === 'no-domain' || reason === 'verification-pending' || reason === 'unknown') {
        commit('SET_DOMAIN_RESTRICTED', true)
      }
      else {
        commit('SET_DOMAIN_RESTRICTED', false)
      }
      if (reason === 'restricted-domain') {
        commit('SET_EMAIL_DOMAIN_RESTRICTED', true)
      }
      else {
        commit('SET_EMAIL_DOMAIN_RESTRICTED', false)
      }

      return true
    }
    catch (error: any) {
      console.error('FAILED TO VERIFY IF DOMAIN IS RESTRICTED OR UNAUTHENTICATED', `(${promoterOid}) ${emailAddress} - `, error?.message ? error.message : error)
      commit('SET_EMAIL_DOMAIN_RESTRICTED', false)
      return false
    }
  },

  async FETCH_MESSAGE_LINKS({ commit, rootState }, messageOid) {
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    const { $axios, $arNotification } = getContext()

    if (isNaN(messageOid)) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch message links' })
      return null
    }
    const uri = `/task/${messageOid}/messages-short-urls`
    commit('SET_IS_FETCHING_MESSAGE_LINKS', true)
    try {
      const params = {}

      const { data } = await $axios.get(uri, params)
      commit('SET_MESSAGE_LINKS', data)

      return true
    }
    catch (error) {
      $arNotification.push({ type: 'error', message: 'Failed to fetch message links' })
      return false
    }
    finally {
      commit('SET_IS_FETCHING_MESSAGE_LINKS', false)
    }
  },

  async MESSAGES_EXPORT_CSV({ commit, rootState }, { messageProvider, eventOids, dateStart, dateEnd }) {
    if (!rootState.auth.account) { return }
    const { promoterOid } = rootState.auth.account
    const uri = `/promoter/${promoterOid}/messages-export`
    const { $axios, $arNotification } = getContext()

    try {
      const { data } = await $axios.post(uri, {
        messageProvider, eventOids, dateStart, dateEnd,
      })
      this.$arNotification.push({ type: 'success', message: `${rootState.auth.account.emailAddress} will receive an email with a download link shortly` })
      return data
    }
    catch (error) {
      this.$arNotification.push({ type: 'error', message: 'Failed to Export CSV' })
      return false
    }
  },
}
